import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { extractApiError } from '../../../lib/api';
import {
  getTBMByToken,
  signByToken,
  TBM,
  TBMPhoto,
  getTBMStatusLabel,
  getTBMStatusBadgeTone,
} from '../../../lib/tbm';
import { resolveTbmEducationDoc } from '../../../lib/tbmEducationDoc';
import { SinglePhotoViewer } from '../../../components/inspection/shared/PhotoViewer';
import { SignaturePadWithRef, SignaturePadRef } from '../../../components/SignaturePad';
import { LocaleProvider, useLocale } from '../../../lib/i18n/useLocale';
import { LanguageSwitcher } from '../../../components/i18n/LanguageSwitcher';
import { useDynamicTranslation } from '../../../lib/i18n/useDynamicTranslation';
import { collectTbmDynamicTexts } from '../../../lib/i18n/collectTbmDynamicTexts';
import { AlertTriangleIcon as ExclamationIcon, CheckIcon } from '../../../components/icons';
import { getFindingSeverityTone } from '../../../lib/inspections';
import { Badge, Button, Card } from '@safetyhub/ui';

export default function TBMSignPage() {
  return (
    <LocaleProvider>
      <TBMSign />
    </LocaleProvider>
  );
}

function TBMSign() {
  const { token } = useParams<{ token: string }>();
  const { t, locale } = useLocale();

  const [tbm, setTBM] = useState<TBM | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // 서명 폼
  const [name, setName] = useState('');
  const [isSigning, setIsSigning] = useState(false);
  const [signSuccess, setSignSuccess] = useState(false);

  // 사진 뷰어
  const [viewerPhoto, setViewerPhoto] = useState<{ url: string; caption?: string } | null>(null);

  // 서명 패드
  const signaturePadRef = useRef<SignaturePadRef>(null);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    if (token) {
      loadTBM();
    }
  }, [token]);

  const loadTBM = async () => {
    if (!token) return;

    setIsLoading(true);
    setError('');
    try {
      const data = await getTBMByToken(token, new URLSearchParams(window.location.search).get('via'));
      setTBM(data);
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getSignatureData = () => {
    if (!hasSignature || !signaturePadRef.current) return undefined;
    return signaturePadRef.current.toDataURL();
  };

  const handleSign = async () => {
    if (!token || !name.trim()) return;

    setIsSigning(true);
    setError('');
    try {
      const signatureData = getSignatureData();
      await signByToken(token, name.trim(), {
        signatureMethod: hasSignature ? 'DRAWN' : 'TYPED',
        signatureData,
        signatureSource: 'worker_self',
        viewedLanguage: locale,
      });
      setSignSuccess(true);
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsSigning(false);
    }
  };

  /**
   * 문서 읽기의 단일 파생 — 이 화면은 `tbm.content`를 직접 읽지 않는다.
   *
   * ⚠️ 종전엔 `content.photos`·`content.hazardItems`·`content.hazards`·`content.criticalHazard`를
   * 직접 읽었는데, **COMPOSED 문서엔 그 키가 하나도 없다**(dev 실측: 그 네 키 0건 / `-src` 형상만
   * 존재). 그래서 근로자가 이 화면에서 위험요인·대책·중점위험요인·사진을 **통째로 못 봤다**.
   * 교육 화면은 같은 결함을 이 파생으로 이미 닫았고(`lib/tbmEducationDoc.ts`), 이 화면만 남아
   * 있었다. 렌더 구조(3단 폴백·중점 강조)는 그대로 두고 **재료만** 바꾼다.
   */
  const doc = useMemo(() => resolveTbmEducationDoc(tbm), [tbm]);

  // 사진 정규화 (photoId 기준 그룹핑) — hooks는 early return 전에 호출
  const normalizedPhotos = useMemo((): TBMPhoto[] => {
    const raw = doc.photos;
    if (raw.length === 0) return [];

    const hasOldFormat = raw.some((p: any) => !p.findings && p.description);
    if (!hasOldFormat) return raw;

    const hazards = doc.hazards;
    const measures = doc.safetyMeasures;
    const measuresPerHazard = hazards.length > 0
      ? Math.ceil(measures.length / hazards.length)
      : measures.length;
    const hazardMeasuresMap = new Map<string, string[]>();
    hazards.forEach((h, i) => {
      const start = i * measuresPerHazard;
      const end = Math.min(start + measuresPerHazard, measures.length);
      hazardMeasuresMap.set(h, measures.slice(start, end));
    });

    const map = new Map<string, TBMPhoto>();
    for (const p of raw) {
      const desc = (p as any).description as string | undefined;
      const existing = map.get(p.photoId);
      const matchedMeasures = desc ? hazardMeasuresMap.get(desc) : undefined;
      const finding = desc
        ? { description: desc, measures: matchedMeasures && matchedMeasures.length > 0 ? matchedMeasures : undefined }
        : undefined;
      if (existing) {
        if (finding) {
          existing.findings = [...(existing.findings || []), finding];
        }
      } else {
        map.set(p.photoId, {
          photoId: p.photoId,
          photoUrl: p.photoUrl,
          source: p.source,
          caption: p.caption,
          findings: finding ? [finding] : undefined,
        });
      }
    }
    return Array.from(map.values());
  }, [doc.photos, doc.hazards, doc.safetyMeasures]);

  // 중점위험요인 (COMPOSED는 criticalHazardText+criticalHazardMeasures가 파생에서 합쳐진다)
  const ch = doc.criticalHazard;
  const chText = ch?.hazard || '';
  const chMeasureSet = useMemo(() => new Set(
    ch?.selectedMeasures?.length ? ch.selectedMeasures : ch?.measure ? [ch.measure] : []
  ), [ch]);

  // 동적 번역 수집기는 구 content 키를 구조적으로 읽으므로 **파생을 그 형상으로 넘긴다** —
  // tbm을 그대로 주면 COMPOSED에서 위험요인·대책·중점위험요인이 통째로 번역 대상에서 빠진다
  // (교육 화면 `TbmEducateView`와 같은 규약).
  const dynamicTexts = useMemo(
    () =>
      collectTbmDynamicTexts(
        {
          title: tbm?.title,
          content: {
            workType: doc.workType,
            workDescription: doc.workDescription,
            specialNotes: doc.savedSpecialNotes,
            hazards: doc.hazards,
            safetyMeasures: doc.safetyMeasures,
            hazardItems: doc.hazardItems,
            criticalHazard: doc.criticalHazard,
          },
        },
        normalizedPhotos,
      ),
    [tbm?.title, doc, normalizedPhotos],
  );

  const { getTranslated } = useDynamicTranslation(dynamicTexts);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-soft">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
          <p className="mt-4 text-ink-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error && !tbm) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-soft">
        <Card
          radius="lg"
          padding="none"
          elevation="md"
          className="max-w-md w-full p-8 text-center"
        >
          <div className="h-16 w-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
            <ExclamationIcon className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="mt-4 text-xl font-semibold text-ink-900">{t('sign.invalidToken')}</h1>
          <p className="mt-2 text-ink-600">{error}</p>
          <p className="mt-4 text-sm text-ink-500">{t('sign.expired')}</p>
        </Card>
      </div>
    );
  }

  if (signSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-soft">
        <Card
          radius="lg"
          padding="none"
          elevation="md"
          className="max-w-md w-full p-8 text-center"
        >
          <div className="h-16 w-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
            <CheckIcon className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="mt-4 text-xl font-semibold text-ink-900">{t('sign.success')}</h1>
          <p className="mt-2 text-ink-600">{t('sign.legalNotice')}</p>
        </Card>
      </div>
    );
  }

  if (!tbm) {
    return null;
  }

  return (
    <div className="min-h-screen bg-surface-soft py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* TBM 정보 */}
        <Card radius="lg" padding="xl" elevation="md" className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-ink-900">{t('sign.title')}</h1>
            <div className="flex items-center gap-2">
              <LanguageSwitcher dropUp={false} />
              <Badge tone={getTBMStatusBadgeTone(tbm.status)}>
                {getTBMStatusLabel(tbm.status, locale)}
              </Badge>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <span className="text-sm text-ink-500">{t('tbm.contents')}</span>
              <p className="text-sm font-medium text-ink-900">{getTranslated(tbm.title)}</p>
            </div>
            <div>
              <span className="text-sm text-ink-500">{t('tbm.location')}</span>
              <p className="text-sm font-medium text-ink-900">{tbm.site?.name}</p>
            </div>
            <div>
              <span className="text-sm text-ink-500">{t('tbm.signedAt')}</span>
              <p className="text-sm font-medium text-ink-900">
                {new Date(tbm.createdAt).toLocaleString('ko-KR', {})}
              </p>
            </div>
          </div>
        </Card>

        {/* TBM 내용 */}
        {tbm.content && (
          <Card radius="lg" padding="none" elevation="md" className="overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-line">
              <h2 className="text-lg font-medium text-ink-900">{t('tbm.contents')}</h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              {doc.workType && (
                <div>
                  <span className="text-sm font-medium text-ink-700">{t('tbm.workType')}</span>
                  <p className="text-sm text-ink-900">{getTranslated(doc.workType)}</p>
                </div>
              )}

              {/* 특이사항은 **구 문서의 content 값**이다(COMPOSED는 세션 축이라 이 칸이 빈다) —
                  파생이 주는 것 이상을 여기서 지어내지 않는다. 근로자 화면에 없던 정보를
                  새로 얹지 않는 것이 이번 변경의 규약이다. */}
              {doc.savedSpecialNotes && (
                <div>
                  <span className="text-sm font-medium text-ink-700">{t('tbm.specialNotes')}</span>
                  <p className="text-sm text-ink-900">{getTranslated(doc.savedSpecialNotes)}</p>
                </div>
              )}

              {/* 사진 기반 위험요인 표시 */}
              {normalizedPhotos.length > 0 ? (
                <div className="space-y-4 pt-2">
                  {normalizedPhotos.map((photo, idx) => {
                    const isCriticalPhoto = chText && photo.findings?.some((f) => f.description === chText);
                    return (
                      <div key={`${photo.photoId}-${idx}`} className={`border-2 rounded-card overflow-hidden ${isCriticalPhoto ? 'border-red-300' : 'border-line'}`}>
                        <button
                          type="button"
                          onClick={() => setViewerPhoto({ url: photo.photoUrl, caption: photo.caption })}
                          className="w-full h-44 overflow-hidden"
                        >
                          <img
                            src={photo.photoUrl}
                            alt={photo.caption ? getTranslated(photo.caption) : `${t('tbm.findings')} ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                        <div className="p-3 space-y-2">
                          {photo.caption && (
                            <p className="text-sm font-medium text-ink-700">{getTranslated(photo.caption)}</p>
                          )}
                          {photo.findings && photo.findings.length > 0 ? (
                            <div className="divide-y divide-line-soft">
                              {photo.findings.map((finding, fIdx) => {
                                const isCritical = chText && finding.description === chText;
                                return (
                                <div key={fIdx} className={fIdx > 0 ? 'pt-2' : ''}>
                                  <div className="flex items-start gap-2">
                                    <span className="shrink-0 mt-0.5 w-4 h-4 rounded bg-red-100 text-red-600 flex items-center justify-center">
                                      <WarningIcon className="w-2.5 h-2.5" />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        {finding.severity && (
                                          <SeverityBadge severity={finding.severity} className="shrink-0" />
                                        )}
                                        {isCritical && (
                                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-600 text-white text-xs font-bold">
                                            <WarningIcon className="w-2.5 h-2.5" />
                                            {t('manager.criticalHazard')}
                                          </span>
                                        )}
                                      </div>
                                      <span className="text-sm text-ink-900">{getTranslated(finding.description)}</span>
                                    </div>
                                  </div>
                                  {finding.measures && finding.measures.length > 0 && (
                                    <div className="flex items-start gap-2 mt-1.5 ml-0.5">
                                      <span className="shrink-0 mt-0.5 w-4 h-4 rounded bg-green-100 text-green-600 flex items-center justify-center">
                                        <ShieldIcon className="w-2.5 h-2.5" />
                                      </span>
                                      <ul className="space-y-0.5">
                                        {finding.measures.map((m, mIdx) => (
                                          <li key={mIdx} className={`text-sm ${isCritical && chMeasureSet.has(m) ? 'font-bold text-ink-900' : 'text-ink-700'}`}>{getTranslated(m)}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : doc.hazardItems.length > 0 ? (
                /* hazardItems 기반 표시 (COMPOSED는 tbmHazards-src가 이 형상으로 파생된다) */
                <div className="space-y-3">
                  {doc.hazardItems.map((item, idx) => {
                    const isCritical = chText && item.hazard === chText;
                    const itemMeasures = (item.selectedMeasures && item.selectedMeasures.length > 0) ? item.selectedMeasures : item.measure ? [item.measure] : [];
                    return (
                      <div key={idx} className={`rounded-card p-3 ${isCritical ? 'border-2 border-red-300' : 'border border-line'}`}>
                        <div className="flex items-start gap-2">
                          <span className="shrink-0 mt-0.5 w-4 h-4 rounded bg-red-100 text-red-600 flex items-center justify-center">
                            <WarningIcon className="w-2.5 h-2.5" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                              {item.severity && <SeverityBadge severity={item.severity} />}
                              {isCritical && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-600 text-white text-xs font-bold">
                                  <WarningIcon className="w-2.5 h-2.5" />
                                  {t('manager.criticalHazard')}
                                </span>
                              )}
                            </div>
                            <span className="text-sm text-ink-900">{getTranslated(item.hazard)}</span>
                          </div>
                        </div>
                        {itemMeasures.length > 0 && (
                          <div className="flex items-start gap-2 mt-1.5 ml-0.5">
                            <span className="shrink-0 mt-0.5 w-4 h-4 rounded bg-green-100 text-green-600 flex items-center justify-center">
                              <ShieldIcon className="w-2.5 h-2.5" />
                            </span>
                            <ul className="space-y-0.5">
                              {itemMeasures.map((m, mi) => (
                                <li key={mi} className={`text-sm ${isCritical && chMeasureSet.has(m) ? 'font-bold text-ink-900' : 'text-ink-700'}`}>{getTranslated(m)}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* 사진도 hazardItems도 없으면: 기존 텍스트 방식 */
                <>
                  {doc.hazards.length > 0 && (
                    <div>
                      <span className="text-sm font-medium text-ink-700">{t('tbm.findings')}</span>
                      <ul className="mt-1 list-disc list-inside text-sm text-ink-900">
                        {doc.hazards.map((hazard, idx) => (
                          <li key={idx}>{hazard}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {doc.safetyMeasures.length > 0 && (
                    <div>
                      <span className="text-sm font-medium text-ink-700">{t('tbm.safetyMeasures')}</span>
                      <ul className="mt-1 list-disc list-inside text-sm text-ink-900">
                        {doc.safetyMeasures.map((measure, idx) => (
                          <li key={idx}>{measure}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>
        )}

        {/* 서명 폼 */}
        <Card radius="lg" padding="xl" elevation="md">
          <h2 className="text-lg font-medium text-ink-900 mb-4">{t('sign.description')}</h2>

          {/* 에러 */}
          {error && (
            <div className="mb-4 rounded-card bg-red-50 p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            {/* 이름 입력 */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-ink-700">
                {t('sign.namePlaceholder')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500"
                placeholder={t('sign.namePlaceholder')}
              />
            </div>

            {/* 서명 캔버스 */}
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">
                {t('sign.signaturePlaceholder')}
              </label>
              <SignaturePadWithRef
                ref={signaturePadRef}
                onSignatureChange={setHasSignature}
                height={150}
              />
            </div>

            {/* 서명 버튼 */}
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={handleSign}
              disabled={isSigning || !name.trim()}
            >
              {isSigning ? t('common.loading') : t('sign.signButton')}
            </Button>

            <p className="text-xs text-ink-500 text-center">
              {t('sign.legalNotice')}
            </p>
          </div>
        </Card>
      </div>

      {/* 사진 뷰어 */}
      <SinglePhotoViewer
        url={viewerPhoto?.url || ''}
        caption={viewerPhoto?.caption}
        isOpen={!!viewerPhoto}
        onClose={() => setViewerPhoto(null)}
      />
    </div>
  );
}

// Icons
function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

/**
 * 심각도 배지 — 이 화면 두 자리(사진 발견사항 / 위험요인 목록)가 같은 뜻을 각자 적고 있어
 * 하나로 합쳤다. 색은 `getFindingSeverityTone`이 정본이고, 라벨만 여기서 고른다.
 * TbmEducateView·educator 페이지의 동명 컴포넌트와 규약이 같다.
 */
function SeverityBadge({ severity, className }: { severity: string; className?: string }) {
  const { t } = useLocale();
  const label =
    severity === 'CRITICAL' ? t('badge.severity.critical') : severity === 'HIGH' ? t('badge.severity.high') : severity === 'MEDIUM' ? t('badge.severity.medium') : t('badge.severity.low');
  return (
    <Badge tone={getFindingSeverityTone(severity)} shape="rounded" className={className}>
      {label}
    </Badge>
  );
}
